import { z } from "zod";
import { runtimeUiDocumentSchema, runtimeUiElementSchema } from "../ui/types";
import {
  neighbourhoodTemplateSchema,
  resourceNodeDefinitionSchema,
  resourceSpawnPolicySchema,
  validateNeighbourhoodTemplate,
} from "../../shared/neighbourhood";
import {
  BUILDING_KEYS,
  BUILDING_POWER_BY_LEVEL,
  KNIGHT_LEVEL_TABLE,
  MARCH_CAPACITY_BY_CASTLE_LEVEL,
  TRINKET_TIER_RANGES,
  TROOP_DEFINITIONS,
  TROOP_TIER_UNLOCK_LEVEL,
  WEAPON_LEVEL_TABLE,
} from "../../shared/core/balanceTables";

const keySchema = z.string().regex(/^[a-z][a-z0-9_]*$/);
const nonNegativeInteger = z.number().int().nonnegative();
const positiveInteger = z.number().int().positive();
const percentage = z.number().finite().nonnegative();
const contentVersionSchema = z.union([
  positiveInteger,
  z.string().regex(/^draft:[^\s]+$/, "Draft versions must start with draft:"),
]);
const assetSchema = z.object({
  id: z.string().min(1), key: z.string().min(1), name: z.string().min(1),
  versionId: z.string().min(1).nullable(), immutablePath: z.string().min(1).nullable().optional(),
  url: z.string().min(1).optional(),
}).strict();
const uiSchema = z.object({
  documents: z.record(z.string(), runtimeUiDocumentSchema),
  elementsByDocument: z.record(z.string(), z.array(runtimeUiElementSchema)),
}).strict();
const bondInteractionSchema = z.object({
  id: z.string().uuid(), key: keySchema, knightId: z.string().uuid(), knightKey: keySchema,
  knightName: z.string().min(1), bondLevel: positiveInteger, title: z.string().min(1),
  triggerKey: z.enum(["talk", "level_up", "mission_victory", "raid_victory", "manual"]),
  onceOnly: z.boolean(), illustrationAssetId: z.string().uuid().nullable(),
  rewardTable: z.object({ id: z.string().uuid(), key: keySchema, name: z.string().min(1) }).strict().nullable(),
  lines: z.array(z.object({ id: z.string().uuid(), speaker: z.enum(["knight", "princess", "narrator"]), text: z.string().min(1) }).strict()).min(2).max(5),
}).strict();
const princessCosmeticSchema = z.object({
  id: z.string().uuid(), key: keySchema, name: z.string().min(1), description: z.string(),
  slot: z.enum(["body", "dress", "hair", "crown", "accessory", "background"]),
  rarity: z.enum(["common", "uncommon", "rare", "epic", "legendary"]),
  assetId: z.string().uuid().nullable(), layerKey: keySchema, enabled: z.boolean(),
  advancedTransform: z.object({ offsetX: z.number().finite(), offsetY: z.number().finite(), scale: z.number().min(0.1).max(10) }).strict().nullable(),
}).strict();
const denounceAccusationSchema = z.object({
  id: z.string().uuid(), key: keySchema, label: z.string().min(1),
  iconAssetId: z.string().uuid().nullable(), enabled: z.boolean(), sortOrder: nonNegativeInteger,
}).strict();
const castlePvpSnapshotSchema = z.object({
  contractVersion: z.literal(1),
  scoutDurationSeconds: z.literal(600),
  scoutPolicyKey: z.enum(["FREE", "GOLD"]).nullable(),
  scoutCostGold: nonNegativeInteger,
  scoutProductionEnabled: z.boolean(),
  raidProductionEnabled: z.boolean(),
  raidLootPct: z.literal(15),
  raidLoadOrder: z.array(z.enum(["food", "wood", "stone", "gold"])).length(4).nullable(),
  sameTargetDailyCap: z.literal(3),
}).strict();
const socialSnapshotSchema = z.object({
  contractVersion: z.literal(1),
  productionEnabled: z.boolean(),
  denounceCooldownSeconds: z.literal(86400),
  denounceDurationSeconds: z.literal(86400),
  envoyCooldownSeconds: z.literal(86400),
  envoyDurationSeconds: z.literal(86400),
  globalActionCap: positiveInteger.nullable(),
  envoyPresets: z.record(z.string(), z.object({
    id: z.string().uuid(), key: keySchema, title: z.string().min(1), message: z.string().min(1).max(280),
    presentationKey: keySchema, relationshipState: z.enum(["INFORMAL", "BETRAYED"]),
    iconAssetId: z.string().uuid().nullable(), enabled: z.boolean(), archived: z.boolean(),
  }).strict()),
}).strict();
const resourceAmountsSchema = z
  .object({
    food: nonNegativeInteger,
    wood: nonNegativeInteger,
    stone: nonNegativeInteger,
    gold: nonNegativeInteger,
  })
  .strict();

const coreKnightSchema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    role: z.string(),
    premium: z.boolean(),
    fullBodyAssetId: z.string().uuid().nullable(),
    portraitAssetId: z.string().uuid().nullable(),
    enabled: z.boolean(),
  })
  .strict();

const knightLevelSchema = z
  .object({
    level: z.number().int().min(1).max(30),
    attack: nonNegativeInteger,
    defence: nonNegativeInteger,
    hp: positiveInteger,
    power: nonNegativeInteger,
  })
  .strict();

const weaponFamilySchema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    knightId: z.string().uuid(),
    enabled: z.boolean(),
  })
  .strict();

const weaponLevelSchema = z
  .object({
    requiredLevel: z.number().int().min(1).max(30),
    attack: nonNegativeInteger,
    defence: nonNegativeInteger,
    power: nonNegativeInteger,
    standardRarity: z.enum(["normal", "rare", "exotic"]).nullable(),
  })
  .strict();

const weaponSchema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    description: z.string(),
    familyId: z.string().uuid(),
    requiredLevel: z.number().int().min(1).max(30),
    rarity: z.enum(["normal", "rare", "exotic", "legendary"]),
    attack: nonNegativeInteger,
    defence: nonNegativeInteger,
    power: nonNegativeInteger,
    artAssetId: z.string().uuid().nullable(),
    enabled: z.boolean(),
  })
  .strict();

const trinketTierSchema = z
  .object({
    tier: z.number().int().min(1).max(5),
    castleLevelMin: z.number().int().min(1).max(20),
    castleLevelMax: z.number().int().min(1).max(20),
    attackPct: z.tuple([percentage, percentage]),
    defencePct: z.tuple([percentage, percentage]),
    hpPct: z.tuple([percentage, percentage]),
  })
  .strict();

const trinketSchema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    description: z.string(),
    tier: z.number().int().min(1).max(5),
    attackPct: z.tuple([percentage, percentage]),
    defencePct: z.tuple([percentage, percentage]),
    hpPct: z.tuple([percentage, percentage]),
    artAssetId: z.string().uuid().nullable(),
    enabled: z.boolean(),
  })
  .strict();

const troopSchema = z
  .object({
    id: z.string().uuid(),
    troopClass: z.enum(["infantry", "archers", "cavalry"]),
    tier: z.number().int().min(1).max(4),
    unlockBuildingLevel: z.number().int().min(1).max(20),
    combatPower: positiveInteger,
    kingdomPower: positiveInteger,
    attack: positiveInteger,
    defence: positiveInteger,
    hp: positiveInteger,
    load: positiveInteger,
    foodUpkeepPerHour: z.number().finite().nonnegative(),
    trainSeconds: positiveInteger,
    trainCost: resourceAmountsSchema,
    healCost: resourceAmountsSchema,
    enabled: z.boolean(),
  })
  .strict();

const buildingSchema = z
  .object({
    key: z.enum([
      "main_castle",
      "farm",
      "lumbermill",
      "quarry",
      "gold_mine",
      "treasury",
      "barracks",
      "archery_range",
      "stable",
      "infirmary",
    ]),
    name: z.string().min(1),
    category: z.enum([
      "castle",
      "resource",
      "treasury",
      "military",
      "infirmary",
    ]),
    maxLevel: z.literal(20),
    upgradeEnabled: z.boolean(),
    passiveProductionEnabled: z.boolean(),
  })
  .strict();

const buildingPowerSchema = z
  .object({
    level: z.number().int().min(1).max(20),
    mainCastle: nonNegativeInteger,
    eachResource: nonNegativeInteger,
    treasury: nonNegativeInteger,
    eachMilitary: nonNegativeInteger,
    infirmary: nonNegativeInteger,
    allTen: nonNegativeInteger,
  })
  .strict();

const resourceSchema = z
  .object({
    key: z.enum(["food", "wood", "stone", "gold", "gems"]),
    name: z.string().min(1),
    premium: z.boolean(),
    securedEligible: z.boolean(),
    displayOrder: nonNegativeInteger,
  })
  .strict();

const featureGateSchema = z
  .object({
    featureKey: keySchema,
    startsEnabled: z.boolean(),
    reason: z.string().min(1),
  })
  .strict();

const rewardTargetSchema = z
  .object({
    type: z.enum(["resource", "weapon", "trinket", "cosmetic"]),
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
  })
  .strict();

const rewardTableSchema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    description: z.string(),
    groups: z.array(
      z
        .object({
          id: z.string().uuid(),
          name: z.string().min(1),
          mode: z.enum(["all", "weighted_one", "weighted_n"]),
          draws: positiveInteger,
          entries: z.array(
            z
              .object({
                id: z.string().uuid(),
                weight: positiveInteger,
                noReward: z.boolean(),
                target: rewardTargetSchema.nullable(),
                minAmount: nonNegativeInteger,
                maxAmount: nonNegativeInteger,
              })
              .strict(),
          ),
        })
        .strict(),
    ),
  })
  .strict();

const combatProfileV2Schema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    description: z.string(),
    profileKey: z.enum([
      "pvp_city_attacker",
      "pvp_city_defender",
      "world_node",
      "world_hunt",
      "black_keep_zero",
      "mission_zero",
      "expedition_readiness",
      "kingdom_threat_zero",
    ]),
    resolutionKind: z.enum(["battle", "readiness", "cooperative_damage"]),
    countersEnabled: z.boolean(),
    mutatesRealTroops: z.boolean(),
    threatScalar: z.number().finite().nonnegative().nullable(),
    productionEnabled: z.boolean(),
  })
  .strict();

const enemyV2Schema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    description: z.string(),
    enemyType: z.enum(["normal", "elite", "boss"]),
    artAssetId: z.string().uuid().nullable(),
    backgroundAssetId: z.string().uuid().nullable(),
    attack: nonNegativeInteger,
    defence: nonNegativeInteger,
    hp: positiveInteger,
    powerOverride: nonNegativeInteger.nullable(),
    preCounterPower: nonNegativeInteger.nullable(),
    combatTroops: z.array(
      z
        .object({
          troopClass: z.enum(["infantry", "archers", "cavalry"]),
          tier: z.number().int().min(1).max(4),
          count: positiveInteger,
        })
        .strict(),
    ),
    combatEnabled: z.boolean(),
    enabled: z.boolean(),
    tags: z.array(
      z
        .object({
          key: z.enum(["armored", "boss", "magic", "corrupted", "duelist"]),
          name: z.string().min(1),
        })
        .strict(),
    ),
  })
  .strict();

const modifierV2Schema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
    description: z.string(),
    scope: z.enum(["encounter", "expedition", "account", "knight"]),
    statKey: z.enum(["attack", "defence", "hp", "build_speed", "reward_bonus"]),
    operation: z.enum(["none", "add", "multiply", "percent", "set"]),
    value: z.number().finite(),
    durationKey: z.enum(["instant", "encounter", "expedition", "permanent"]),
    iconAssetId: z.string().uuid().nullable(),
    stackingKey: keySchema.nullable(),
    stackOrder: z.number().int().nullable(),
    productionEnabled: z.boolean(),
  })
  .strict();

const phase3ReferenceSchema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    name: z.string().min(1),
  })
  .strict();

const missionV2Schema = z
  .object({
    id: z.string().uuid(),
    key: keySchema,
    title: z.string().min(1),
    description: z.string(),
    resultText: z.string(),
    category: z.enum(["escort", "combat", "defence", "special", "resource"]),
    durationSeconds: positiveInteger.max(604800),
    knightsMin: z.number().int().min(1).max(3),
    knightsMax: z.number().int().min(1).max(3),
    requirement: z
      .object({
        kind: z.enum(["knight_power", "final_attack", "final_defence", "final_hp"]),
        operator: z.literal("sum_gte"),
        threshold: nonNegativeInteger,
      })
      .strict(),
    illustrationAssetId: z.string().uuid(),
    rewardTable: phase3ReferenceSchema,
    availability: z.discriminatedUnion("type", [
      z.object({ type: z.literal("always") }).strict(),
      z.object({ type: z.literal("weekend") }).strict(),
    ]),
    definitionVersion: positiveInteger,
    enabled: z.boolean(),
  })
  .strict()
  .superRefine((mission, context) => {
    if (mission.knightsMax < mission.knightsMin)
      context.addIssue({
        code: "custom",
        path: ["knightsMax"],
        message: "Maximum party size cannot be below minimum party size.",
      });
  });

const missionBoardV2Schema = z
  .object({
    refreshSeconds: positiveInteger.max(604800),
    shownSlots: z.literal(4),
    noDuplicates: z.boolean(),
    pools: z.array(
      z.object({
        id: z.string().uuid(),
        key: keySchema,
        name: z.string().min(1),
        entries: z.array(
          z.object({
            id: z.string().uuid(),
            missionId: z.string().uuid(),
            missionKey: keySchema,
            missionTitle: z.string().min(1),
            weight: positiveInteger,
          }).strict(),
        ),
      }).strict(),
    ),
    slots: z.array(
      z.object({
        id: z.string().uuid(),
        key: keySchema,
        slotIndex: z.number().int().min(1).max(4),
        label: z.string().min(1),
        poolId: z.string().uuid(),
      }).strict(),
    ),
  })
  .strict()
  .superRefine((board, context) => {
    if (board.slots.length !== 0 && board.slots.length !== 4)
      context.addIssue({ code: "custom", path: ["slots"], message: "Enabled Mission Board requires exactly four slots." });
    const indices = [...board.slots].map((slot) => slot.slotIndex).sort((a, b) => a - b);
    if (indices.length === 4 && indices.join(",") !== "1,2,3,4")
      context.addIssue({ code: "custom", path: ["slots"], message: "Mission slot indices must be exactly 1, 2, 3, and 4." });
  });

const blackKeepV2Schema = z
  .object({
    maxFloor: nonNegativeInteger,
    teamSize: z.literal(3),
    attempts: z.literal("Unlimited"),
    floors: z.array(
      z.object({
        id: z.string().uuid(),
        floor: positiveInteger,
        boss: z.boolean(),
        enemy: phase3ReferenceSchema,
        recommendedPower: nonNegativeInteger,
        modifier: phase3ReferenceSchema.nullable(),
        backgroundAssetId: z.string().uuid().nullable(),
        introText: z.string(),
        firstClearReward: phase3ReferenceSchema,
        repeatReward: phase3ReferenceSchema.nullable(),
        definitionVersion: positiveInteger,
        enabled: z.boolean(),
      }).strict(),
    ),
  })
  .strict()
  .superRefine((keep, context) => {
    const enabled = keep.floors.filter((floor) => floor.enabled).sort((a, b) => a.floor - b.floor);
    enabled.forEach((floor, index) => {
      if (floor.floor !== index + 1)
        context.addIssue({ code: "custom", path: ["floors", index, "floor"], message: `Missing explicit enabled Floor ${index + 1}.` });
    });
    if (keep.maxFloor !== enabled.length)
      context.addIssue({ code: "custom", path: ["maxFloor"], message: "Black Keep maxFloor must equal its contiguous enabled catalogue." });
  });

const expeditionChoiceV2Schema = z.object({
  id: z.string().uuid(),
  label: z.string().min(1),
  checkType: z.enum(["none", "army_attack", "army_defence", "effective_power"]),
  requirement: nonNegativeInteger,
  successModifier: phase3ReferenceSchema.nullable(),
  failureModifier: phase3ReferenceSchema.nullable(),
  successNodeId: z.string().uuid(),
  failureNodeId: z.string().uuid(),
  successReadinessEffect: z.enum(["none", "successful_check", "safe_route", "risky_treasure", "rest", "major_recovery"]),
  successReadinessDelta: z.number().int().min(-8).max(25),
  failureReadinessEffect: z.enum(["none", "failed_check"]),
  failureReadinessDelta: z.number().int().min(-6).max(0),
  successRewardTable: phase3ReferenceSchema.nullable(),
  failureRewardTable: phase3ReferenceSchema.nullable(),
  sortOrder: nonNegativeInteger,
}).strict();

const expeditionNodeV2Schema = z.object({
  id: z.string().uuid(), key: keySchema,
  nodeKind: z.enum(["combat", "boss", "choice", "path_fork", "rest", "recovery", "loot", "check", "event", "final"]),
  title: z.string().min(1), bodyText: z.string(), sceneAssetId: z.string().uuid().nullable(),
  enemy: phase3ReferenceSchema.nullable(), rewardTable: phase3ReferenceSchema.nullable(),
  failureModifier: phase3ReferenceSchema.nullable(), graphX: z.number().finite().nonnegative(), graphY: z.number().finite().nonnegative(),
  mapX: z.number().finite().nonnegative(), mapY: z.number().finite().nonnegative(), isFinal: z.boolean(),
  readinessEffect: z.enum(["none", "successful_check", "failed_check", "safe_route", "risky_treasure", "rest", "major_recovery"]),
  readinessDelta: z.number().int().min(-8).max(25), repeatable: z.boolean(), sortOrder: nonNegativeInteger,
  choices: z.array(expeditionChoiceV2Schema),
}).strict().superRefine((node, context) => {
  if (node.nodeKind === "choice" && node.choices.length !== 2)
    context.addIssue({ code: "custom", path: ["choices"], message: "Normal choice encounter requires exactly two choices." });
  if (node.nodeKind !== "choice" && node.choices.length > 0)
    context.addIssue({ code: "custom", path: ["choices"], message: "Only a normal choice encounter may own Choice A/B records." });
});

const expeditionV2Schema = z.object({
  id: z.string().uuid(), key: keySchema, name: z.string().min(1), summary: z.string(),
  difficulty: z.enum(["easy", "normal", "hard", "heroic"]), recommendedPower: nonNegativeInteger,
  partySize: z.literal(3), troopsRequired: z.literal(true), finalRewardTable: phase3ReferenceSchema,
  boardCardAssetId: z.string().uuid().nullable(), detailAssetId: z.string().uuid().nullable(), mapAssetId: z.string().uuid(),
  definitionVersion: z.number().int().min(2), availability: z.record(z.string(), z.unknown()), enabled: z.boolean(),
  nodes: z.array(expeditionNodeV2Schema),
  edges: z.array(z.object({ id: z.string().uuid(), fromNodeId: z.string().uuid(), toNodeId: z.string().uuid(),
    label: z.string(), conditionKey: z.enum(["always", "pass", "fail"]), sortOrder: nonNegativeInteger }).strict()),
}).strict();

const expeditionBoardV2Schema = z.object({
  visibleSlots: positiveInteger.max(20), refreshSeconds: positiveInteger.max(604800),
  listings: z.array(z.object({ id: z.string().uuid(), expeditionId: z.string().uuid(), enabled: z.boolean(),
    weight: positiveInteger, sortOrder: nonNegativeInteger }).strict()),
}).strict();

const retainedContentSchema = z.object({
  contentVersion: contentVersionSchema,
  assets: z.record(z.string(), assetSchema),
  ui: uiSchema,
  bonds: z.record(z.string(), bondInteractionSchema).default({}),
  cosmetics: z.record(z.string(), princessCosmeticSchema).default({}),
  denounceAccusations: z.record(z.string(), denounceAccusationSchema).default({}),
}).strict();

export const contentSnapshotV2Schema = retainedContentSchema
  .extend({
    schemaVersion: z.literal(2),
    rewards: z.record(z.string(), rewardTableSchema),
    enemies: z.record(z.string(), enemyV2Schema),
    modifiers: z.record(z.string(), modifierV2Schema),
    combatProfiles: z.record(z.string(), combatProfileV2Schema),
    missions: z.record(z.string(), missionV2Schema),
    missionBoard: missionBoardV2Schema,
    blackKeep: blackKeepV2Schema,
    expeditions: z.record(z.string(), expeditionV2Schema),
    expeditionBoard: expeditionBoardV2Schema,
    neighbourhoodTemplates: z
      .record(z.string(), neighbourhoodTemplateSchema)
      .default({}),
    activeNeighbourhoodTemplateKey: keySchema.nullable().default(null),
    mapPve: z.object({
      worldHunts: z.record(z.string(), z.object({ id: z.string().uuid(), key: keySchema, name: z.string().min(1),
        enemyId: z.string().uuid(), levelMin: positiveInteger, levelMax: positiveInteger, respawnSeconds: positiveInteger,
        rewardTableId: z.string().uuid(), affinityLabel: z.string(), mapIconAssetId: z.string().uuid(), contentVersion: positiveInteger,
        productionEnabled: z.boolean() }).strict()),
      kingdomThreats: z.record(z.string(), z.object({ id: z.string().uuid(), key: keySchema, name: z.string().min(1),
        maxHp: positiveInteger, attemptsPerDay: z.literal(3), damageProfileId: z.string().uuid(), artAssetId: z.string().uuid(),
        attemptRewardTableId: z.string().uuid(), clearRewardTableId: z.string().uuid(), scheduleKey: keySchema.nullable(),
        participationPolicy: z.enum(["ANY_CONTRIBUTION", "MIN_DAMAGE"]).nullable(), minimumContribution: positiveInteger.nullable(),
        contentVersion: positiveInteger, productionEnabled: z.boolean(), milestones: z.array(z.object({ id: z.string().uuid(),
          thresholdPct: positiveInteger.max(100), rewardTableId: z.string().uuid(), sortOrder: nonNegativeInteger }).strict()) }).strict()),
    }).strict().default({ worldHunts: {}, kingdomThreats: {} }),
    resourceNodes: z.object({
      definitions: z.record(z.string(), resourceNodeDefinitionSchema),
      spawnPolicy: resourceSpawnPolicySchema.nullable(),
    }).strict().default({ definitions: {}, spawnPolicy: null }),
    castlePvp: castlePvpSnapshotSchema.default({
      contractVersion: 1, scoutDurationSeconds: 600, scoutPolicyKey: null, scoutCostGold: 0,
      scoutProductionEnabled: false, raidProductionEnabled: false, raidLootPct: 15,
      raidLoadOrder: null, sameTargetDailyCap: 3,
    }),
    social: socialSnapshotSchema.default({
      contractVersion: 1, productionEnabled: false,
      denounceCooldownSeconds: 86400, denounceDurationSeconds: 86400,
      envoyCooldownSeconds: 86400, envoyDurationSeconds: 86400,
      globalActionCap: null, envoyPresets: {},
    }),
    core: z
      .object({
        formulaVersion: z.literal("core-v2.0.0"),
        balanceKey: z.string().min(1),
        deferredConfig: z.record(z.string(), z.unknown()),
        knights: z.record(z.string(), coreKnightSchema),
        knightLevels: z.array(knightLevelSchema).length(30),
        weaponFamilies: z.record(z.string(), weaponFamilySchema),
        weaponLevels: z.array(weaponLevelSchema).length(30),
        weapons: z.record(z.string(), weaponSchema),
        trinketTiers: z.array(trinketTierSchema).length(5),
        trinkets: z.record(z.string(), trinketSchema),
        troops: z.array(troopSchema).length(12),
        buildings: z.record(z.string(), buildingSchema),
        buildingPower: z.array(buildingPowerSchema).length(20),
        marchCapacity: z
          .array(
            z
              .object({
                castleLevel: z.number().int().min(1).max(20),
                capacity: positiveInteger,
              })
              .strict(),
          )
          .length(20),
        resources: z.record(z.string(), resourceSchema),
        featureGates: z.record(z.string(), featureGateSchema),
      })
      .strict(),
  })
  .strict()
  .superRefine((snapshot, context) => {
    if (snapshot.castlePvp.raidLoadOrder !== null && new Set(snapshot.castlePvp.raidLoadOrder).size !== 4)
      context.addIssue({ code: "custom", path: ["castlePvp", "raidLoadOrder"], message: "Raid Load order must contain each normal resource exactly once." });
    if (snapshot.castlePvp.scoutProductionEnabled && snapshot.castlePvp.scoutPolicyKey === null)
      context.addIssue({ code: "custom", path: ["castlePvp", "scoutPolicyKey"], message: "Production Scout requires an approved policy." });
    if (snapshot.castlePvp.raidProductionEnabled && snapshot.castlePvp.raidLoadOrder === null)
      context.addIssue({ code: "custom", path: ["castlePvp", "raidLoadOrder"], message: "Production Raid requires an approved Load order." });
    if (snapshot.social.productionEnabled && !Object.values(snapshot.social.envoyPresets).some((preset) => preset.enabled && !preset.archived))
      context.addIssue({ code: "custom", path: ["social", "envoyPresets"], message: "Production social actions require at least one reviewed Envoy preset." });
    for (const [key, enemy] of Object.entries(snapshot.enemies)) {
      if (enemy.combatEnabled && enemy.preCounterPower === null)
        context.addIssue({
          code: "custom",
          path: ["enemies", key, "preCounterPower"],
          message: "Combat-enabled Enemy requires explicit Pre-Counter Power.",
        });
    }
    for (const [key, modifier] of Object.entries(snapshot.modifiers)) {
      if (
        modifier.productionEnabled &&
        (modifier.stackingKey === null || modifier.stackOrder === null)
      )
        context.addIssue({
          code: "custom",
          path: ["modifiers", key, "stackingKey"],
          message:
            "Production Modifier requires explicit stacking key and order.",
        });
    }
    if (
      snapshot.activeNeighbourhoodTemplateKey !== null &&
      !snapshot.neighbourhoodTemplates[snapshot.activeNeighbourhoodTemplateKey]
    )
      context.addIssue({
        code: "custom",
        path: ["activeNeighbourhoodTemplateKey"],
        message: "Active Neighbourhood template key does not exist.",
      });
    for (const [key, template] of Object.entries(snapshot.neighbourhoodTemplates)) {
      for (const issue of validateNeighbourhoodTemplate(template)) {
        if (!template.productionReady && issue.code !== "NEIGHBOURHOOD_IMPORTED_DRAFT") continue;
        context.addIssue({
          code: "custom",
          path: ["neighbourhoodTemplates", key, ...issue.path.split(".")],
          message: issue.message,
        });
      }
      if (template.contractVersion === 2) {
        const definitionIds = new Set(Object.values(snapshot.resourceNodes.definitions).map((definition) => definition.id));
        for (const [locationIndex, location] of template.locations.entries()) {
          for (const definitionId of location.allowedResourceDefinitionIds) if (!definitionIds.has(definitionId))
            context.addIssue({ code: "custom", path: ["neighbourhoodTemplates", key, "locations", locationIndex, "allowedResourceDefinitionIds"], message: "Resource slot references a missing Resource Node definition." });
        }
      }
    }
    if (snapshot.core.featureGates.resource_node?.startsEnabled) {
      if (!snapshot.resourceNodes.spawnPolicy?.productionEnabled)
        context.addIssue({ code: "custom", path: ["resourceNodes", "spawnPolicy"], message: "Enabled Resource Nodes require a production-enabled spawn policy." });
      if (!Object.values(snapshot.resourceNodes.definitions).some((definition) => definition.productionEnabled && !definition.archived))
        context.addIssue({ code: "custom", path: ["resourceNodes", "definitions"], message: "Enabled Resource Nodes require at least one reviewed production definition." });
      if (snapshot.activeNeighbourhoodTemplateKey === null)
        context.addIssue({ code: "custom", path: ["activeNeighbourhoodTemplateKey"], message: "Enabled Resource Nodes require an active Neighbourhood template." });
    }
  });

export type ContentSnapshotV2 = z.infer<typeof contentSnapshotV2Schema>;
export type PublishedContentSnapshot = ContentSnapshotV2;

const buildingNames: Record<(typeof BUILDING_KEYS)[number], string> = {
  main_castle: "Main Castle", farm: "Farm", lumbermill: "Lumbermill",
  quarry: "Quarry", gold_mine: "Gold Mine", treasury: "Treasury",
  barracks: "Barracks", archery_range: "Archery Range", stable: "Stable",
  infirmary: "Infirmary",
};

const buildingCategory = (key: (typeof BUILDING_KEYS)[number]) =>
  key === "main_castle" ? "castle" as const
    : ["farm", "lumbermill", "quarry", "gold_mine"].includes(key) ? "resource" as const
      : key === "treasury" ? "treasury" as const
        : key === "infirmary" ? "infirmary" as const : "military" as const;

/** Canonical, schema-valid base for editor previews and tests. */
export function createEmptyContentSnapshotV2(
  contentVersion: ContentSnapshotV2["contentVersion"] = "draft:empty",
): ContentSnapshotV2 {
  const resources = [
    ["food", "Food", false, true, 10], ["wood", "Wood", false, true, 20],
    ["stone", "Stone", false, true, 30], ["gold", "Gold", false, true, 40],
    ["gems", "Gems", true, false, 50],
  ] as const;
  const snapshot = {
    schemaVersion: 2 as const, contentVersion, assets: {},
    ui: { documents: {}, elementsByDocument: {} }, bonds: {}, cosmetics: {},
    denounceAccusations: {}, rewards: {}, enemies: {}, modifiers: {}, combatProfiles: {},
    missions: {}, missionBoard: { refreshSeconds: 14400, shownSlots: 4 as const, noDuplicates: true, pools: [], slots: [] },
    blackKeep: { maxFloor: 0, teamSize: 3 as const, attempts: "Unlimited" as const, floors: [] },
    expeditions: {}, expeditionBoard: { visibleSlots: 2, refreshSeconds: 3600, listings: [] },
    neighbourhoodTemplates: {}, activeNeighbourhoodTemplateKey: null,
    mapPve: { worldHunts: {}, kingdomThreats: {} },
    resourceNodes: { definitions: {}, spawnPolicy: null },
    castlePvp: { contractVersion: 1 as const, scoutDurationSeconds: 600 as const, scoutPolicyKey: null,
      scoutCostGold: 0, scoutProductionEnabled: false, raidProductionEnabled: false,
      raidLootPct: 15 as const, raidLoadOrder: null, sameTargetDailyCap: 3 as const },
    social: { contractVersion: 1 as const, productionEnabled: false,
      denounceCooldownSeconds: 86400 as const, denounceDurationSeconds: 86400 as const,
      envoyCooldownSeconds: 86400 as const, envoyDurationSeconds: 86400 as const,
      globalActionCap: null, envoyPresets: {} },
    core: {
      formulaVersion: "core-v2.0.0" as const, balanceKey: "canonical_draft",
      deferredConfig: {}, knights: {}, knightLevels: KNIGHT_LEVEL_TABLE.map((row) => ({ ...row })),
      weaponFamilies: {}, weaponLevels: WEAPON_LEVEL_TABLE.map((row) => ({
        ...row, standardRarity: row.requiredLevel >= 25 ? "exotic" as const : null,
      })), weapons: {},
      trinketTiers: Object.entries(TRINKET_TIER_RANGES).map(([tier, row]) => ({
        tier: Number(tier), castleLevelMin: row.castleMin, castleLevelMax: row.castleMax,
        attackPct: [...row.balanced.attackPct], defencePct: [...row.balanced.defencePct], hpPct: [...row.balanced.hpPct],
      })), trinkets: {},
      troops: TROOP_DEFINITIONS.map((row, index) => ({
        id: `00000000-0000-4000-8000-${String(index + 1).padStart(12, "0")}`,
        ...row, unlockBuildingLevel: TROOP_TIER_UNLOCK_LEVEL[row.tier], enabled: true,
      })),
      buildings: Object.fromEntries(BUILDING_KEYS.map((key) => [key, {
        key, name: buildingNames[key], category: buildingCategory(key), maxLevel: 20 as const,
        upgradeEnabled: ["main_castle", "barracks", "archery_range", "stable", "infirmary"].includes(key),
        passiveProductionEnabled: false,
      }])),
      buildingPower: BUILDING_POWER_BY_LEVEL.map((row) => ({ ...row })),
      marchCapacity: MARCH_CAPACITY_BY_CASTLE_LEVEL.map((capacity, index) => ({ castleLevel: index + 1, capacity })),
      resources: Object.fromEntries(resources.map(([key, name, premium, securedEligible, displayOrder]) =>
        [key, { key, name, premium, securedEligible, displayOrder }])),
      featureGates: {},
    },
  };
  return parseContentSnapshotV2(snapshot);
}

function invalidSnapshotMessage(error: z.ZodError) {
  return error.issues
    .slice(0, 4)
    .map((issue) => `${issue.path.join(".") || "snapshot"}: ${issue.message}`)
    .join("; ");
}

export function parseContentSnapshotV2(value: unknown): ContentSnapshotV2 {
  const result = contentSnapshotV2Schema.safeParse(value);
  if (!result.success)
    throw new Error(
      `Content snapshot v2 is invalid: ${invalidSnapshotMessage(result.error)}`,
    );
  return result.data;
}

export function parsePublishedContentSnapshot(
  value: unknown,
): PublishedContentSnapshot {
  if (!value || typeof value !== "object" || (value as { schemaVersion?: unknown }).schemaVersion !== 2)
    throw new Error("Published content uses an unsupported snapshot schema version.");
  // Releases 3 and 4 are v2 but predate the Phase 11 contract cleanup. Strip
  // only their empty/read-only compatibility members before strict parsing.
  const {
    worldHunts: _worldHunts,
    worldHuntSlots: _worldHuntSlots,
    kingdomThreats: _kingdomThreats,
    pvp: _pvp,
    ...current
  } = value as Record<string, unknown>;
  // Releases 3 and 4 were published before modifier stacking metadata became
  // mandatory. Keep the immutable bytes untouched and hydrate only the
  // documented reader defaults at the compatibility boundary.
  const legacyModifiers = current.modifiers && typeof current.modifiers === "object"
    ? Object.fromEntries(Object.entries(current.modifiers as Record<string, unknown>).map(([key, modifier]) => [
      key,
      modifier && typeof modifier === "object"
        ? {
            stackingKey: null,
            stackOrder: null,
            productionEnabled: false,
            ...(modifier as Record<string, unknown>),
          }
        : modifier,
    ]))
    : current.modifiers;
  const legacyProfileMap: Record<string, { profileKey: "mission_zero" | "black_keep_zero" | "kingdom_threat_zero"; resolutionKind: "battle" | "cooperative_damage" }> = {
    power_standard: { profileKey: "mission_zero", resolutionKind: "battle" },
    atk_weighted: { profileKey: "mission_zero", resolutionKind: "battle" },
    def_weighted: { profileKey: "black_keep_zero", resolutionKind: "battle" },
    boss_damage: { profileKey: "kingdom_threat_zero", resolutionKind: "cooperative_damage" },
  };
  const legacyCombatProfiles = current.combatProfiles && typeof current.combatProfiles === "object"
    ? Object.fromEntries(Object.entries(current.combatProfiles as Record<string, unknown>).map(([key, profile]) => {
      if (!profile || typeof profile !== "object") return [key, profile];
      const row = profile as Record<string, unknown>;
      if (row.profileKey !== undefined) return [key, row];
      const mapped = legacyProfileMap[String(row.formulaKey ?? key)] ?? legacyProfileMap.power_standard!;
      return [key, {
        id: row.id,
        key: row.key,
        name: row.name,
        description: row.description,
        ...mapped,
        countersEnabled: false,
        mutatesRealTroops: false,
        threatScalar: mapped.profileKey === "kingdom_threat_zero" ? 1 : null,
        productionEnabled: false,
      }];
    }))
    : current.combatProfiles;
  const legacyContract = typeof current.contentVersion === "number" && current.contentVersion <= 4;
  return parseContentSnapshotV2({
    ...current,
    modifiers: legacyModifiers,
    combatProfiles: legacyCombatProfiles,
    ...(legacyContract ? {
      // These pre-overhaul rows used incompatible party sizes, route models and
      // activity semantics. They remain immutable in Storage but are safely
      // gated when a rollback release is read by the current runtime.
      missionBoard: { refreshSeconds: 60, shownSlots: 4, noDuplicates: true, pools: [], slots: [] },
      blackKeep: { maxFloor: 0, teamSize: 3, attempts: "Unlimited", floors: [] },
      expeditions: {},
      expeditionBoard: { visibleSlots: 1, refreshSeconds: 60, listings: [] },
    } : {}),
  });
}
